import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { backendConfig } from '../config/backendConfig';
import ArtmosphereNavbar from '../components/ArtmosphereNavbar';

export default function MentorSetupFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [mentorCount, setMentorCount] = useState(1);
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Initialize mentor count from signup or location state
  useEffect(() => {
    if (location.state?.mentorCount) {
      setMentorCount(location.state.mentorCount);
      // Initialize mentors array with empty objects
      const initialMentors = Array.from({ length: location.state.mentorCount }, (_, index) => ({
        id: index + 1,
        name: '',
        email: '',
        accessCode: ''
      }));
      setMentors(initialMentors);
    }
  }, [location.state]);

  // Check if user came from signup/payment or add mentor flow
  useEffect(() => {
    if (location.state?.isNewOrganization && user) {
      console.log('New organization mentor setup started for', mentorCount, 'mentors');
    } else if (location.state?.isAddMentorFlow && user) {
      console.log('Add mentor flow started for', mentorCount, 'additional mentors');
    } else if (!user) {
      navigate('/login');
    }
  }, [location.state, user, navigate]);

  const updateMentor = (index, field, value) => {
    setMentors(prev => prev.map((mentor, i) => 
      i === index ? { ...mentor, [field]: value } : mentor
    ));
  };

  const generateAccessCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const handleNext = () => {
    if (currentStep === 1) {
      // Validate mentor details
      const hasEmptyFields = mentors.some(mentor => !mentor.name.trim() || !mentor.email.trim());
      if (hasEmptyFields) {
        setError('Please fill in all mentor names and emails');
        return;
      }
      
      // Generate access codes for all mentors
      const mentorsWithCodes = mentors.map(mentor => ({
        ...mentor,
        accessCode: generateAccessCode()
      }));
      setMentors(mentorsWithCodes);
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
    setError('');
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  const handleConfirmMentors = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Create mentor accounts and send emails for each mentor
      const results = [];
      for (const mentor of mentors) {
        const response = await fetch(`${backendConfig.baseUrl}/api/mentors/invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ownerId: user.uid,
            mentorEmail: mentor.email,
            mentorName: mentor.name,
            isAddMentorFlow: location.state?.isAddMentorFlow || false
          })
        });

        const result = await response.json();
        
        if (!response.ok) {
          throw new Error(result.error || `Failed to create account for ${mentor.name}`);
        }
        
        results.push(result);
      }

      setSuccess(location.state?.isAddMentorFlow 
        ? '✅ Additional mentor accounts created successfully! Emails sent to all new mentors.'
        : '✅ Mentor accounts created successfully! Emails sent to all mentors.'
      );
      
      // Redirect to organization dashboard after 2 seconds
      setTimeout(() => {
        navigate('/organization-dashboard');
      }, 2000);

    } catch (err) {
      setError(err.message || 'Failed to create mentor accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8E1]">
      <ArtmosphereNavbar />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-4">
              {[1, 2, 3].map((step) => (
                <React.Fragment key={step}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                    currentStep >= step 
                      ? 'bg-amber-600 text-white' 
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step}
                  </div>
                  {step < 3 && (
                    <div className={`w-16 h-1 ${
                      currentStep > step ? 'bg-amber-600' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="flex justify-center mt-2">
              <span className="text-sm text-gray-600">
                Step {currentStep} of 3
              </span>
            </div>
          </div>

          {/* Step 1: Mentor Details */}
          {currentStep === 1 && (
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {location.state?.isAddMentorFlow ? 'Add New Mentors' : 'Add Your Mentors'}
              </h2>
              <p className="text-gray-600 mb-8">
                {location.state?.isAddMentorFlow 
                  ? `Enter the details for the ${mentorCount} additional mentor${mentorCount > 1 ? 's' : ''} you're adding to your organization.`
                  : `Enter the details for your ${mentorCount} mentor${mentorCount > 1 ? 's' : ''}.`
                }
              </p>
              
              <div className="space-y-6">
                {mentors.map((mentor, index) => (
                  <div key={mentor.id} className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Mentor {index + 1}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                        <input
                          type="text"
                          value={mentor.name}
                          onChange={(e) => updateMentor(index, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                          placeholder="Enter mentor's full name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                        <input
                          type="email"
                          value={mentor.email}
                          onChange={(e) => updateMentor(index, 'email', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                          placeholder="Enter mentor's email"
                          required
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="flex justify-end mt-8">
                <button
                  onClick={handleNext}
                  className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                >
                  Next: Review Details
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Review Mentor Details */}
          {currentStep === 2 && (
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Review Mentor Details</h2>
              <p className="text-gray-600 mb-8">Please review the mentor information before creating accounts.</p>
              
              <div className="space-y-4">
                {mentors.map((mentor, index) => (
                  <div key={mentor.id} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Mentor {index + 1}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Name:</span>
                        <p className="font-medium">{mentor.name}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Email:</span>
                        <p className="font-medium">{mentor.email}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Access Code:</span>
                        <p className="font-mono font-medium text-amber-600">{mentor.accessCode}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-between mt-8">
                <button
                  onClick={handleBack}
                  className="px-6 py-3 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleNext}
                  className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                >
                  Next: Create Accounts
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Create Accounts */}
          {currentStep === 3 && (
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Create Mentor Accounts</h2>
              <p className="text-gray-600 mb-8">We'll create accounts for your mentors and send them access codes via email.</p>
              
              {success && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                  <p className="text-sm text-green-700">{success}</p>
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-semibold text-amber-900 mb-4">What happens next:</h3>
                <ul className="space-y-2 text-sm text-amber-800">
                  <li>• Mentor accounts will be created with their access codes</li>
                  <li>• Each mentor will receive an email with their access code</li>
                  <li>• You'll receive a confirmation email with all access codes</li>
                  <li>• You'll be redirected to your organization dashboard</li>
                </ul>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={handleBack}
                  className="px-6 py-3 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleConfirmMentors}
                  disabled={loading}
                  className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating Accounts...' : 'Create Accounts & Send Emails'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}